// dev-garage — "Your Garage" YMM vehicle finder (homepage). Figma node 380:13873.
// ───────────────────────────────────────────────────────────────────────────
// Mounts as <dev-garage>. A single "Choose Type" selector cascades
// Type → Year → Make → Model: clicking it opens a dropdown of the options
// reachable from the prior picks; each pick advances the prompt and drops an
// editable chip (click a chip to jump back and change that level). Built on the
// Marqo public API:
//   window.Marqo.getYMMData({ parts })  — next-level options from #fitment-vehicles
//   window.Marqo.handleizeYMM(query)    — selection → { shopUrl, vehicleName, … }
// On "Shop Now" it sends the shopper to the results page with the fitment deep
// link, and persists the vehicle to localStorage['devGarage'] (shared with the
// PDP picker, assets/dev-shop-my-ride.js).
//
// buildFilter() (marqo.js) translates the fitments deep link into the best
// filter the index supports: scope ∈ {Guaranteed Fit, Universal Fit} AND ride type
// ∈ {vehicle's type, Universal} — narrows to the vehicle TYPE and drops non-fitment
// apparel. Exact Year/Make/Model filtering needs per-vehicle data in the index.

(function () {
  'use strict';

  const NAME = 'dev-garage';
  if (customElements.get(NAME)) return;

  // YMM levels, in cascade order. `key` matches Marqo's YMM_LEVELS / handleizeYMM
  // param names; `label` drives the "Choose <label>" prompt.
  const LEVELS = [
    { key: 'type', label: 'Type' },
    { key: 'year', label: 'Year' },
    { key: 'make', label: 'Make' },
    { key: 'model', label: 'Model' },
  ];
  const SEARCH_THRESHOLD = 7; // show the dropdown filter once a step has more than this
  const GARAGE_KEY = 'devGarage'; // saved garage list, shared with blocks/dev-shop-my-ride
  const SELECTION_KEY = 'devGarageSelection'; // this selector's live state (persists "empty")
  const GARAGE_MAX = 20;

  // Resolve window.Marqo (assets/marqo.js loads async) — poll briefly.
  function whenMarqo(timeout = 4000) {
    return new Promise((resolve) => {
      if (window.Marqo && window.Marqo.getYMMData) return resolve(window.Marqo);
      const start = Date.now();
      const t = setInterval(() => {
        if (window.Marqo && window.Marqo.getYMMData) { clearInterval(t); resolve(window.Marqo); }
        else if (Date.now() - start > timeout) { clearInterval(t); resolve(null); }
      }, 100);
    });
  }

  customElements.define(NAME, class extends HTMLElement {
    #marqo = null;
    #initialized = false;
    #selected = [];     // [{ key, label, value }] — chosen so far, in cascade order
    #options = [];       // current step's full (unfiltered) option list
    #editIndex = null;   // level index the open dropdown is choosing for
    #open = false;
    #openSeq = 0;        // cancels an in-flight option load when the dropdown closes
    #onDocClick = null;
    #onKeydown = null;
    #onHashChange = null;
    #onAnchorClick = null;
    #anchorEls = [];

    async connectedCallback() {
      if (this.#initialized) return;
      this.#initialized = true;

      this.resultsUrl = this.dataset.resultsUrl || '/collections/all';
      this.toggleBtn = this.querySelector('[data-garage-toggle]');
      this.labelEl = this.querySelector('[data-garage-label]');
      this.dropdownEl = this.querySelector('[data-garage-dropdown]');
      this.optionsEl = this.querySelector('[data-garage-options]');
      this.searchEl = this.querySelector('[data-garage-search]');
      this.emptyEl = this.querySelector('[data-garage-empty]');
      this.chipsEl = this.querySelector('[data-garage-chips]');
      this.shopBtn = this.querySelector('[data-garage-shop]');
      this.clearBtn = this.querySelector('[data-garage-clear]');
      this.promptDefault = this.labelEl ? this.labelEl.textContent.trim() : 'Choose Vehicle';
      // "Can't find your vehicle?" → My Garage popup
      this.findOpenBtn = this.querySelector('[data-garage-find-open]');
      this.findModalEl = this.querySelector('[data-smr-find-modal]');
      this.findRowsEl = this.querySelector('[data-garage-find-rows]');
      this.findRowTpl = this.querySelector('[data-garage-find-row-tpl]');
      this.findAddBtn = this.querySelector('[data-garage-find-add]');
      if (!this.toggleBtn || !this.optionsEl) return;

      this.#marqo = await whenMarqo();
      if (!this.#marqo) { this.#fail(); return; }

      this.#bind();
      await this.#restore(); // best-effort pre-fill from the saved garage
      this.#render();
      this.#bindAnchorOpen();
    }

    // The hero's "Shop By Vehicle" button links to #<selector-wrap-id> — beyond
    // the native scroll-into-view and the :target CSS highlight (dev-garage.css),
    // also pop the dropdown open so the anchor actually starts the flow. Reads
    // the id off the DOM instead of hardcoding it, so it can't drift out of sync
    // with the id set in sections/dev-garage.liquid.
    #bindAnchorOpen() {
      const wrap = this.toggleBtn.closest('[id]');
      if (!wrap) return;
      const targetHash = '#' + wrap.id;
      // A complete selection already has its vehicle — the anchor should just
      // scroll/highlight it (native href + :target CSS), not pop the dropdown
      // back open uninvited.
      const openIfNeeded = () => { if (!this.#open && !this.#isComplete()) this.#openDropdown(true); };

      // A click on the SAME hash the URL already has never fires 'hashchange'
      // (the fragment doesn't actually change), so listening for hashchange
      // alone only ever catches the first click. Binding directly to any link
      // that targets this anchor makes every click reopen it, while leaving
      // the hash/:target behavior itself untouched. Must stopPropagation —
      // otherwise the click keeps bubbling to document's click-outside-closes
      // listener (#onDocClick), which immediately closes what this just opened
      // (the toggle/shop buttons already guard against this same trap).
      this.#onAnchorClick = (e) => { e.stopPropagation(); openIfNeeded(); };
      this.#anchorEls = Array.from(document.querySelectorAll(`a[href="${targetHash}"]`));
      this.#anchorEls.forEach((a) => a.addEventListener('click', this.#onAnchorClick));

      // Fresh page load / cross-page link arriving with the hash already set.
      this.#onHashChange = () => {
        if (window.location.hash === targetHash) openIfNeeded();
      };
      this.#onHashChange();
      window.addEventListener('hashchange', this.#onHashChange);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.#onDocClick);
      document.removeEventListener('keydown', this.#onKeydown);
      window.removeEventListener('hashchange', this.#onHashChange);
      this.#anchorEls.forEach((a) => a.removeEventListener('click', this.#onAnchorClick));
    }

    #bind() {
      this.toggleBtn.addEventListener('click', (e) => { e.stopPropagation(); this.#toggle(); });
      if (this.shopBtn) this.shopBtn.addEventListener('click', (e) => { e.stopPropagation(); this.#onShopClick(); });
      if (this.clearBtn) this.clearBtn.addEventListener('click', () => this.#resetTo(0));
      if (this.searchEl) this.searchEl.addEventListener('input', () => this.#renderOptions(this.searchEl.value));
      this.dropdownEl.addEventListener('click', (e) => e.stopPropagation());

      // "Can't find your vehicle?" → My Garage popup
      if (this.findOpenBtn) this.findOpenBtn.addEventListener('click', () => this.#openFind());
      if (this.findModalEl) {
        this.findModalEl.querySelectorAll('[data-garage-find-close]').forEach((el) => el.addEventListener('click', () => this.#closeFind()));
      }
      if (this.findAddBtn) this.findAddBtn.addEventListener('click', () => this.#addRide());

      this.#onDocClick = (e) => {
        if (this.#open && !this.dropdownEl.contains(e.target) && !this.toggleBtn.contains(e.target)) this.#close();
      };
      this.#onKeydown = (e) => {
        if (e.key !== 'Escape') return;
        if (this.findModalEl && !this.findModalEl.hidden) this.#closeFind();
        else if (this.#open) this.#close();
      };
      document.addEventListener('click', this.#onDocClick);
      document.addEventListener('keydown', this.#onKeydown);
    }

    #isComplete() { return this.#selected.length >= LEVELS.length; }

    // Next-level options given a prefix of prior picks ([{ value }, …]).
    async #getOptions(prior) {
      const parts = prior.map((s) => ({ value: s.value }));
      try {
        const opts = await this.#marqo.getYMMData(prior.length === 0 ? {} : { parts });
        return Array.isArray(opts) ? opts.filter((o) => o != null && String(o).length) : [];
      } catch (_) { return []; }
    }

    // ── dropdown ───────────────────────────────────────────────────────────
    #toggle() { this.#open ? this.#close() : this.#openDropdown(true); }

    // Open synchronously (so toggle / click-away / Esc always see the right state),
    // then load the options asynchronously under a sequence token.
    #openDropdown(autofocus) {
      // While incomplete the dropdown chooses the next level; once complete,
      // re-opening edits the last level (Model) in place.
      this.#editIndex = this.#isComplete() ? LEVELS.length - 1 : this.#selected.length;
      this.#open = true;
      this.dropdownEl.hidden = false;
      this.toggleBtn.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
      this.#renderLabel();
      this.optionsEl.innerHTML = '';
      if (this.searchEl) { this.searchEl.hidden = true; this.searchEl.value = ''; }
      this.#loadOptions(autofocus);
    }

    async #loadOptions(autofocus) {
      const seq = ++this.#openSeq;
      const idx = this.#editIndex;
      const opts = await this.#getOptions(this.#selected.slice(0, idx));
      if (seq !== this.#openSeq || !this.#open) return; // closed or superseded mid-flight
      // No client-side sort — matches RDI's ymm.js, which just displays the
      // vehicles_v2 metafield tree's own order (getYMMData.js returns it
      // unsorted, identically in both repos; RDI's Type dropdown showing
      // "Snowmobile, ATV, Side-by-Side…" confirms it isn't alphabetized).
      this.#options = opts;
      if (this.searchEl) this.searchEl.hidden = this.#options.length <= SEARCH_THRESHOLD;
      this.#renderOptions('');
      if (autofocus && this.searchEl && !this.searchEl.hidden) setTimeout(() => this.searchEl.focus(), 0);
    }

    #close() {
      this.#open = false;
      this.#editIndex = null;
      this.#openSeq++; // cancel any in-flight option load so it can't re-show the dropdown
      this.dropdownEl.hidden = true;
      this.toggleBtn.setAttribute('aria-expanded', 'false');
      this.classList.remove('is-open');
      this.#renderLabel();
    }

    #renderOptions(filter) {
      const f = String(filter || '').toLowerCase().trim();
      const list = f ? this.#options.filter((o) => String(o).toLowerCase().includes(f)) : this.#options;
      this.optionsEl.innerHTML = '';
      list.forEach((opt) => {
        const li = document.createElement('li');
        li.className = 'dev-garage__option';
        li.setAttribute('role', 'option');
        li.tabIndex = 0;
        li.textContent = opt;
        const pick = () => this.#pick(opt);
        li.addEventListener('click', pick);
        li.addEventListener('keydown', (ev) => {
          if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); pick(); }
        });
        this.optionsEl.appendChild(li);
      });
      if (this.emptyEl) this.emptyEl.hidden = list.length > 0;
    }

    // ── selection ──────────────────────────────────────────────────────────
    #pick(value) {
      const idx = this.#editIndex != null ? this.#editIndex : this.#selected.length;
      this.#selected = this.#selected.slice(0, idx); // replace at idx, drop downstream
      this.#selected.push({ key: LEVELS[idx].key, label: LEVELS[idx].label, value: String(value) });
      this.#render();
      if (this.#isComplete()) {
        this.#close();
      } else {
        // Auto-advance into the next step instead of leaving the shopper to
        // reopen it themselves — openDropdown reads #selected.length fresh,
        // so it naturally lands on whichever level comes right after this pick.
        this.#openDropdown(true);
      }
    }

    #resetTo(index) {
      this.#selected = this.#selected.slice(0, index);
      this.#close();
      this.#render();
    }

    // ── render ─────────────────────────────────────────────────────────────
    #render() {
      this.#renderChips();
      this.#renderLabel();
      this.#renderShop();
      this.#persistSelection(); // remember the live state — incl. "emptied" — across reloads
    }

    #renderChips() {
      if (!this.chipsEl) return;
      this.chipsEl.innerHTML = '';
      this.chipsEl.hidden = this.#selected.length === 0;
      this.#selected.forEach((s, i) => {
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'dev-garage__chip';
        chip.setAttribute('aria-label', `Edit ${s.label}: ${s.value}`);
        const text = document.createElement('span');
        text.className = 'dev-garage__chip-text';
        text.textContent = s.value;
        const x = document.createElement('span');
        x.className = 'dev-garage__chip-remove';
        x.setAttribute('aria-hidden', 'true');
        x.innerHTML = '&times;';
        chip.append(text, x);
        chip.addEventListener('click', (e) => { e.stopPropagation(); this.#resetTo(i); });
        this.chipsEl.appendChild(chip);
      });
    }

    #renderLabel() {
      if (!this.labelEl) return;
      if (this.#isComplete()) {
        this.labelEl.textContent = this.#vehicleName();
      } else if (this.#selected.length === 0) {
        // Before any pick: the friendly "Choose Vehicle" prompt while closed,
        // switching to the actual first step's prompt ("Choose Type") once
        // opened — same pattern the later steps already use once a pick exists.
        this.labelEl.textContent = this.#open ? `Choose ${LEVELS[0].label}` : this.promptDefault;
      } else {
        this.labelEl.textContent = `Choose ${LEVELS[this.#selected.length].label}`;
      }
    }

    #renderShop() {
      if (!this.shopBtn) return;
      // Not a real `disabled` while incomplete — the button stays clickable so
      // #onShopClick can send the shopper back into the dropdown instead of
      // just sitting there inert. aria-disabled still communicates "not ready
      // yet" to assistive tech and drives the dimmed styling (dev-garage.css).
      const enabled = this.#isComplete();
      this.shopBtn.setAttribute('aria-disabled', String(!enabled));
    }

    #onShopClick() {
      if (!this.#isComplete()) { this.#openDropdown(true); return; }
      this.#go();
    }

    #vehicleName() { return this.#selected.map((s) => s.value).join(' '); }

    // ── shop ───────────────────────────────────────────────────────────────
    #go() {
      if (!this.#isComplete()) return;
      const query = new URLSearchParams();
      this.#selected.forEach((s) => query.set(s.key, s.value));

      let deepLink = query.toString();
      try {
        const v = this.#marqo.handleizeYMM(query.toString());
        if (v && v.shopUrl) deepLink = v.shopUrl;
      } catch (_) { /* fall back to the raw query */ }

      this.#save();
      window.location.href = this.#rideHref(this.#selected);
    }

    // Results URL + fitment deep link for a set of YMM parts ([{key|tag, value}]).
    #rideHref(parts) {
      const query = new URLSearchParams();
      (parts || []).forEach((p) => { const k = p.key || p.tag; if (k && p.value) query.set(k, p.value); });
      let deepLink = query.toString();
      try {
        const v = this.#marqo.handleizeYMM(query.toString());
        if (v && v.shopUrl) deepLink = v.shopUrl;
      } catch (_) { /* fall back to the raw query */ }
      const base = this.resultsUrl || '/collections/all';
      return base + (base.includes('?') ? '&' : '?') + deepLink;
    }

    // ── My Garage popup ──────────────────────────────────────────────────────
    #openFind() {
      if (!this.findModalEl) return;
      this.#renderFindRows();
      this.findModalEl.hidden = false;
    }

    #closeFind() {
      if (this.findModalEl) this.findModalEl.hidden = true;
    }

    // "Add Your Ride" → close the popup and start picking in the selector.
    #addRide() {
      this.#closeFind();
      this.#resetTo(0);       // clear the current selection
      this.#openDropdown(true); // open the Type step
    }

    #renderFindRows() {
      if (!this.findRowsEl || !this.findRowTpl) return;
      const rides = this.#readGarage();
      this.findRowsEl.innerHTML = '';
      this.findRowsEl.hidden = rides.length === 0;
      rides.forEach((veh) => {
        const parts = Array.isArray(veh.parts) ? veh.parts : [];
        const name = parts.length ? parts.map((p) => p.value).join(' ') : (veh.name || '');
        const row = this.findRowTpl.content.firstElementChild.cloneNode(true);
        const nameEl = row.querySelector('[data-garage-find-name]');
        const shopEl = row.querySelector('[data-garage-find-shop]');
        const clearEl = row.querySelector('[data-garage-find-clear]');
        if (nameEl) nameEl.textContent = name;
        if (shopEl) shopEl.href = this.#rideHref(parts);
        if (clearEl) clearEl.addEventListener('click', () => this.#clearRide(veh.name));
        this.findRowsEl.appendChild(row);
      });
    }

    #clearRide(name) {
      this.#writeGarage(this.#readGarage().filter((x) => x && x.name !== name));
      this.#renderFindRows();
    }

    // ── localStorage garage (shared with the PDP picker) ─────────────────────
    #readGarage() {
      try { const v = JSON.parse(localStorage.getItem(GARAGE_KEY)); return Array.isArray(v) ? v : []; }
      catch { return []; }
    }
    #writeGarage(list) {
      try { localStorage.setItem(GARAGE_KEY, JSON.stringify(list)); } catch { /* quota / private mode */ }
      document.dispatchEvent(new CustomEvent('dev-garage:change')); // notify cart badges etc.
    }
    #save() {
      const name = this.#vehicleName();
      if (!name) return;
      const parts = this.#selected.map((s) => ({ tag: s.key, value: s.value }));
      const list = this.#readGarage().filter((x) => x && x.name !== name);
      list.unshift({ name, parts });
      this.#writeGarage(list.slice(0, GARAGE_MAX));
    }

    // This selector's live state — written on every change. Stored even when empty
    // so a manually cleared selector stays empty after reload (vs. the saved garage).
    #persistSelection() {
      try {
        localStorage.setItem(SELECTION_KEY, JSON.stringify(this.#selected.map((s) => ({ key: s.key, value: s.value }))));
      } catch { /* quota / private mode */ }
    }
    // Returns the saved live state, or null when it was never set (first visit).
    #readSelection() {
      const raw = (() => { try { return localStorage.getItem(SELECTION_KEY); } catch { return null; } })();
      if (raw == null) return null;
      try { const v = JSON.parse(raw); return Array.isArray(v) ? v : null; } catch { return null; }
    }

    // Restore on load. Prefer this selector's own saved state (which may be an
    // explicit empty = "I cleared it"); only when it was never set do we pre-fill
    // from the most recent saved garage ride. Each level is validated against the
    // live vehicle tree (case-insensitive), stopping at the first gap so a stale or
    // foreign value can never break the cascade.
    async #restore() {
      const own = this.#readSelection();
      const parts = own !== null ? own : (this.#readGarage()[0] || {}).parts || [];
      if (!parts.length) return; // explicit empty, or nothing to pre-fill
      const restored = [];
      for (let i = 0; i < LEVELS.length; i++) {
        const want = parts.find((p) => p && (p.key || p.tag) === LEVELS[i].key);
        if (!want || !want.value) break;
        const opts = await this.#getOptions(restored);
        const match = opts.find((o) => String(o).toLowerCase() === String(want.value).toLowerCase());
        if (!match) break;
        restored.push({ key: LEVELS[i].key, label: LEVELS[i].label, value: String(match) });
      }
      if (restored.length) this.#selected = restored;
    }

    // Marqo unavailable — leave the control visible but inert.
    #fail() {
      if (this.toggleBtn) this.toggleBtn.disabled = true;
      if (this.shopBtn) { this.shopBtn.setAttribute('disabled', ''); this.shopBtn.setAttribute('aria-disabled', 'true'); }
      this.setAttribute('data-unavailable', 'true');
    }
  });
})();
